import React from 'react';
import { Modal, Box, CircularProgress } from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import colors from 'assets/theme/base/colors';
import { formatDate } from 'utils/DateTimeConversion';

/**
 * Same confirmation DealDetail shows before Closed Won: the latest quotation
 * becomes Accepted, all others become Rejected.
 * `summary` is the response of GET /deals/:id/quotations-summary.
 */
const ClosedWonConfirmModal = ({ isOpen, summary, onClose, onConfirm, loading = false }) => (
    <Modal open={isOpen} onClose={loading ? undefined : onClose} aria-labelledby="closed-won-modal-title">
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 550,
                maxWidth: '92vw',
                bgcolor: 'background.paper',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                borderRadius: 3,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ background: colors.info.main, px: 4, py: 3 }}>
                <MDTypography
                    id="closed-won-modal-title"
                    variant="h5"
                    component="h2"
                    sx={{ color: '#FFFFFF', fontWeight: 'bold' }}
                >
                    Confirm Closed Won
                </MDTypography>
            </Box>

            <Box sx={{ p: 4 }}>
                <MDTypography variant="body1" mb={3} sx={{ color: '#344767', lineHeight: 1.6 }}>
                    Marking this deal as <strong>Closed Won</strong> will automatically update the quotation statuses:
                </MDTypography>

                {summary?.has_quotations && (
                    <Box sx={{ mb: 3, borderRadius: 2, border: '1px solid #e3f2fd', overflow: 'hidden' }}>
                        {summary.latest_quotation && (
                            <Box
                                sx={{
                                    p: 2.5,
                                    bgcolor: '#e8f5e9',
                                    borderBottom: summary.other_quotations?.length > 0 ? '1px solid #e0e0e0' : 'none',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            bgcolor: '#4caf50',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                        }}
                                    >
                                        <CheckIcon sx={{ color: '#ffffff', fontSize: 16 }} />
                                    </Box>
                                    <MDTypography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                                        Will be marked as Accepted
                                    </MDTypography>
                                </Box>
                                <MDTypography variant="body1" ml={5.5} sx={{ color: '#1b5e20', fontWeight: 'medium' }}>
                                    {summary.latest_quotation.quote_name}
                                </MDTypography>
                                <MDTypography variant="caption" ml={5.5} sx={{ color: '#558b2f', display: 'block', mt: 0.5 }}>
                                    Last updated: {formatDate(summary.latest_quotation.updated_at, 'datetime')}
                                </MDTypography>
                            </Box>
                        )}

                        {summary.other_quotations?.length > 0 && (
                            <Box sx={{ p: 2.5, bgcolor: '#ffebee' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            bgcolor: '#f44336',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1.5,
                                        }}
                                    >
                                        <CloseIcon sx={{ color: '#ffffff', fontSize: 16 }} />
                                    </Box>
                                    <MDTypography variant="body2" fontWeight="bold" sx={{ color: '#c62828' }}>
                                        Will be marked as Rejected
                                    </MDTypography>
                                </Box>
                                {summary.other_quotations.map((quote) => (
                                    <Box key={quote.id} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                                        <MDTypography variant="body1" ml={5.5} sx={{ color: '#b71c1c', fontWeight: 'medium' }}>
                                            {quote.quote_name}
                                        </MDTypography>
                                        <MDTypography variant="caption" ml={5.5} sx={{ color: '#c62828', display: 'block', mt: 0.5 }}>
                                            Last updated: {formatDate(quote.updated_at, 'datetime')}
                                        </MDTypography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                <MDTypography variant="body2" sx={{ color: '#7b809a', mb: 3, fontStyle: 'italic' }}>
                    Are you sure you want to proceed with this action?
                </MDTypography>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <MDButton onClick={onClose} variant="outlined" color="secondary" disabled={loading}>
                        Cancel
                    </MDButton>
                    <MDButton onClick={onConfirm} variant="gradient" color="info" disabled={loading}>
                        {loading ? <CircularProgress size={18} color="inherit" /> : 'Confirm Closed Won'}
                    </MDButton>
                </Box>
            </Box>
        </Box>
    </Modal>
);

export default ClosedWonConfirmModal;